import { useState, useEffect } from 'react';
import AdminLayout from '../components/admin/AdminLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { MessageSquare, Send, Mail } from 'lucide-react';
import axios from 'axios';
import {
  PageHeader,
  EmptyState,
  LoadingState,
  StatusBadge,
} from '../components/admin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMessages() {
  const { lang, t } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/messages`, { headers: authHeaders() });
      setMessages(r.data.messages);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchMessages(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendReply = async () => {
    if (!replyTo || !replyText.trim()) {
      toast.warning(t('admin.msg_validation'));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/admin/messages/${replyTo}/reply`,
        { reply: replyText },
        { headers: authHeaders() },
      );
      toast.success(t('admin.msg_sent'));
      setReplyTo(null);
      setReplyText('');
      fetchMessages();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSubmitting(false);
    }
  };

  const statusVariant = (status) => {
    if (status === 'unread') return 'info';
    if (status === 'replied') return 'success';
    return 'default';
  };

  return (
    <AdminLayout title="Messages" titleTr={t('admin.msg_page_title')}>
      <PageHeader
        title={t('admin.msg_page_title')}
        subtitle={t('admin.msg_page_subtitle')}
        breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: t('admin.msg_page_title') }]}
      />

      {loading ? (
        <LoadingState variant="table" rows={4} cols={3} />
      ) : messages.length === 0 ? (
        <EmptyState
          icon={<MessageSquare size={40} strokeWidth={1.5} />}
          title={t('admin.msg_empty_title')}
          description={t('admin.msg_empty_desc')}
        />
      ) : (
        <div className="space-y-3">
          {messages.map(m => (
            <div
              key={m.message_id}
              className={`card-clay p-4 ${m.status === 'unread' ? 'border-l-4 border-l-[var(--brand-primary)]' : ''}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[var(--brand-primary)] flex items-center justify-center flex-shrink-0">
                  <Mail size={14} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-bold text-sm">{m.sender_name}</span>
                    <span className="text-[10px] text-[var(--text-muted)]">{m.sender_email}</span>
                    <StatusBadge variant={statusVariant(m.status)} label={m.status} className="ml-auto" />
                  </div>
                  <h4 className="font-bold text-xs mb-1">{m.subject}</h4>
                  <p className="text-xs text-[var(--text-muted)] mb-2">{m.content}</p>
                  {m.reply && (
                    <div className="bg-green-50 rounded-lg p-3 text-xs">
                      <span className="font-bold text-green-600">{t('admin.msg_reply_label')}:</span> {m.reply}
                    </div>
                  )}
                  {!m.reply && (
                    replyTo === m.message_id ? (
                      <div className="flex gap-2 mt-2">
                        <input
                          value={replyText}
                          onChange={e => setReplyText(e.target.value)}
                          placeholder={t('admin.msg_reply_placeholder')}
                          className="flex-1 px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none"
                        />
                        <button
                          onClick={sendReply}
                          disabled={submitting}
                          className="btn-success px-3 py-2 text-sm disabled:opacity-50"
                        >
                          <Send size={14} />
                        </button>
                        <button
                          onClick={() => { setReplyTo(null); setReplyText(''); }}
                          className="px-3 py-2 text-sm text-[var(--text-muted)]"
                        >
                          {t('admin.btn_cancel')}
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setReplyTo(m.message_id)}
                        className="text-xs text-[var(--brand-primary)] font-bold hover:underline mt-1"
                      >
                        {t('admin.msg_reply')}
                      </button>
                    )
                  )}
                  <p className="text-[10px] text-[var(--text-muted)] mt-1">
                    {new Date(m.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
